import React from 'react';
import InfoHintButton from './InfoHintButton';

/**
 * SectionHeader component for consistent section headers
 * Provides consistent styling with optional actions, descriptions, and accent colors
 */
const SectionHeader = ({ 
  title,
  subtitle,
  description,
  infoText,
  infoAriaLabel,
  action,
  variant = 'default', // 'default', 'primary', 'secondary', 'success', 'warning', 'danger'
  size = 'lg', // 'sm', 'md', 'lg', 'xl'
  showAccentBar = true,
  className = '',
  ...props 
}) => {
  // Define color variants for the accent bar
  const getAccentColor = () => {
    switch (variant) {
      case 'primary':
        return 'bg-primary';
      case 'secondary':
        return 'bg-accent';
      case 'success':
        return 'bg-success';
      case 'warning':
        return 'bg-warning';
      case 'danger':
        return 'bg-danger';
      case 'info':
        return 'bg-info';
      default:
        return 'bg-accent';
    }
  };

  // Define text sizes
  const getTextSize = () => {
    switch (size) {
      case 'sm':
        return 'text-base sm:text-lg';
      case 'md':
        return 'text-xl';
      case 'lg':
        return 'text-2xl';
      case 'xl':
        return 'text-3xl';
      default:
        return 'text-2xl';
    }
  };

  return (
    <div className={`mb-3 sm:mb-4 ${className}`} {...props}>
      <div className="flex items-center justify-between">
        <div className="flex items-center min-w-0">
          {showAccentBar && (
            <span className={`inline-block w-1 h-6 sm:h-8 ${getAccentColor()} rounded-full mr-2.5 sm:mr-4 shrink-0`}></span>
          )}
          <div>
            {title && (
              <h2 className={`${getTextSize()} font-bold text-text-primary flex items-center gap-1`}>
                <span className="min-w-0">{title}</span>
                {infoText ? (
                  <InfoHintButton
                    text={infoText}
                    ariaLabel={infoAriaLabel}
                    className="h-6 w-6"
                  />
                ) : null}
              </h2>
            )}
            {subtitle && (
              <p className="text-sm text-text-secondary mt-1">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {action && (
          <div className="flex-shrink-0">
            {action}
          </div>
        )}
      </div>
      {description && (
        <p className="text-sm text-text-muted mt-2 ml-5">
          {description}
        </p>
      )}
    </div>
  );
};

export default SectionHeader;
